<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Brand logo-->
      <b-link class="brand-logo">
        <b-img
          fluid
          src="@/assets/images/logo/Udara360-logo.png"
          width="180"
        />
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        lg="8"
        class="d-none d-lg-flex p-0"
      >
        <div
          v-if="institution.loginPageBackgroundURL"
          class="auth-bg-image"
          role="img"
          aria-label="Institution background"
          :style="{
            backgroundImage: 'url(' + institution.loginPageBackgroundURL + ')',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            width: '100%',
            height: '100%',
          }"
        />
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title class="mb-1">
            Token Authentication
          </b-card-title>

          <b-card-text
            v-if="userData.twoFactorOption === 'SOFTWARE'"
            class="mb-2"
          >
            Please go to your Authenticator app and provide the code to complete your login process
          </b-card-text>
          <b-card-text
            v-else
            class="mb-2"
          >
            We’ve sent a token to your email
            {{ email }}
          </b-card-text>

          <Alert
            :show="showFormError"
            :message="errorMessage"
          />

          <!-- form -->
          <validation-observer ref="loginForm">
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login"
            >
              <!-- Verification code -->
              <InputField
                id="token"
                v-model="userDetails.userToken"
                label="Token"
                placeholder="Enter token"
                type="text"
                error-name="Token"
                rules="required"
              />
              <LinkText
                v-if="!hasResentToken && !isResendingToken && userData.twoFactorOption === 'EMAIL' || userData.twoFactorOption === 'SMS'"
                class="mb-2"
                :on-click="resendToken"
                label="Resend Token"
              />

              <b-spinner
                v-if="!hasResentToken && isResendingToken"
                label="Loading..."
                class="mb-2"
                variant="primary"
              />

              <LinkText
                v-if="hasResentToken && !isResendingToken"
                class="secondary mb-2"
                label="Token has been resent"
              />

              <!-- submit button -->
              <Button
                label="Confirm"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                block
                @click="login"
              />
            </b-form>
          </validation-observer>
          <b-card-text class="text-center mt-2">
            <b-link
              :to="{name:'auth-login'}"
              class="text-primary"
            >
              <feather-icon icon="ChevronLeftIcon" /> Back to login
            </b-link>
          </b-card-text>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BLink, BCardText, BCardTitle, BImg, BForm, BSpinner,
} from 'bootstrap-vue'
import { required } from '@validations'
import { mapActions, mapGetters } from 'vuex'
import { toastAlert } from '@core/mixins/toast'
// eslint-disable-next-line import/no-cycle
import useJwt from '@/auth/jwt/useJwt'
import capitalizeFirstLetter from '../../../helpers/capitalizeFirstLetter'
import InputField from '../../forms/InputField.vue'
import Button from '../../components/button/Button.vue'
import Alert from '../../components/alert/Alert.vue'
import LinkText from '../../components/LinkText.vue'

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    ValidationObserver,
    InputField,
    Button,
    Alert,
    LinkText,
    BSpinner,
  },
  mixins: [toastAlert],
  data() {
    return {
      userDetails: {
        userToken: '',
      },
      userData: '',
      email: '',
      hasResentToken: false,
      isResendingToken: false,
      isLoading: false,
      isDisabled: false,
      showFormError: false,
      errorMessage: '',
      status: '',
      timer: '',
      // validation rules
      required,
    }
  },
  computed: {
    ...mapGetters({
      institution: 'auth/getInstitution',
    }),
  },
  watch: {
    hasResentToken(newValue) {
      if (newValue) {
        // eslint-disable-next-line no-return-assign
        this.timer = setTimeout(() => (this.hasResentToken = false), 1000 * 3)
      }
      if (!newValue) {
        clearTimeout(this.timer)
      }
    },
  },
  mounted() {
    this.getUser()
  },
  methods: {
    ...mapActions({
      loginUser: 'auth/loginUser',
      resendNewToken: 'auth/resendNewToken',
    }),
    async resendToken() {
      try {
        const { user } = this.$route.params
        this.isResendingToken = true
        const response = await this.resendNewToken({ username: user.username })
        const { status } = response
        if (status === 200) {
          this.isResendingToken = false
          this.hasResentToken = true
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.errorMessage = responseError.errorMessage
        })
      }
    },
    getUser() {
      const { user, data } = this.$route.params
      this.userData = data
      if (user.username) {
        this.email = user.username
      } else {
        this.$router.push({ name: 'auth-login' })
      }
    },
    login() {
      this.$refs.loginForm.validate().then(async success => {
        if (success) {
          try {
            this.showFormError = false
            this.isDisabled = true
            this.isLoading = true
            const { user } = this.$route.params
            const response = await this.loginUser({
              userToken: this.userDetails.userToken.trim(),
              username: user.username,
              password: user.password,
            })
            const { userDetails, accessToken, refreshToken } = response.data
            if (userDetails.status.toLowerCase() === 'inactive') {
              this.showFormError = true
              this.errorMessage = 'Your account is Inactive. Kindly contact admin'
              return false
            }
            this.$ability.update([{ action: 'manage', subject: 'all' }])
            useJwt.setToken(accessToken)
            useJwt.setRefreshToken(refreshToken)
            useJwt.setEmail(userDetails.email)
            if (userDetails.forceChangeOfPassword === true) {
              this.$router
                .push({ name: 'setup-security-questions' })
              this.toastAlert({
                title: 'Warning',
                icon: 'CoffeeIcon',
                variant: 'warning',
                text: 'Dear User, please change your Password.',
              })
            } else {
              this.$router
                .push({ name: 'home' })
                .then(() => {
                  this.$store.dispatch('auth/updateSessionState', false)
                  this.toastAlert({
                    title: `${this.$t('Welcome')} ${capitalizeFirstLetter(userDetails.firstname)}
                  ${capitalizeFirstLetter(userDetails.lastname)}`,
                    icon: 'CoffeeIcon',
                    variant: 'success',
                    text: `${this.$t('You have successfully logged in as')} ${userDetails.role}.`,
                  })
                })
            }
            this.isDisabled = false
            this.isLoading = false
            return true
          } catch (error) {
            error.response.data.errors.forEach(responseError => {
              this.errorMessage = responseError.errorMessage
            })
            this.showFormError = true
          } finally {
            this.isDisabled = false
            this.isLoading = false
          }
        }
        return false
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>

<style scoped>
.text-primary {
    color: var(--primary) !important;
  }
</style>
